"use client";
import React, {
  useCallback,
  useReducer,
  memo,
  useState,
  useEffect,
  useRef,
} from "react";
import Image from "next/image";
import Icon from "../icons/Icons";
import { useLoginModal } from "@/utils/useLoginModal";
import CartCountButton from "../ui/CartCountButton";
import { useIsMobile } from "@/utils/useIsMobile";
import useScrollTrigger from "@/utils/useScrollTrigger";
import { usePathname } from "next/navigation";
import useScrollDirection from "@/utils/useScrollDirection";
import { deleteCookie } from "@/utils/authService";
import HeaderSearchModal from "../modal/HeaderSearchModal";
import { useDispatch } from "react-redux";
import { openHelpModal } from "@/redux/features/HelpModal/helpModalSlice";
import { HeaderProps, MenuItem, UiAction, UiState } from "@/type/commonTypes";
import HeaderSidebar from "./HeaderSidebar";
import { BLOG_ROOT, ROOT_URL } from "@/utils/gloableVariables";
import Link from "next/link";

const reducer = (state: UiState, action: UiAction): UiState => {
  switch (action.type) {
    case "toggleMenu":
      return {
        ...state,
        activeMenu: state.activeMenu === action.payload ? null : action.payload,
      };
    case "toggleSidebar":
      return { ...state, isSidebarOpen: !state.isSidebarOpen };
    case "closeSidebar":
      return { ...state, isSidebarOpen: false };
    case "toggleSearch":
      return { ...state, isSearchOpen: !state.isSearchOpen };
    default:
      return state;
  }
};

const HIDDEN_ROUTE_PREFIXES = [
  "/financial-planning/retirement-planning/",
  "/financial-planning/child-future-planning/",
];

const HIDDEN_ROUTES = new Set(["/client-assessment/result", "/login"]);

const FloatingHeader: React.FC<HeaderProps> = memo(
  ({ menuItems, profileData, isSafari }) => {
    const pathName = usePathname();
    const dispatchHelpModal = useDispatch();

    const { open, isOpen, isLoggedIn, setLoggedIn, setModalKey } =
      useLoginModal();
    const [isMounted, setIsMounted] = useState(false);

    const isTabDevice = useIsMobile(991);
    const isScrolled = useScrollTrigger();

    const [isSpecialDate, setIsSpecialDate] = useState(false);
    const [profileMenuItems, setProfileMenuItems] = useState<MenuItem[]>([]);

    useEffect(() => {
      if (isLoggedIn) {
        setProfileMenuItems(profileData.loggedIn);
      } else {
        setProfileMenuItems(profileData.loggedOut);
      }
    }, [profileData, isLoggedIn]);

    useEffect(() => {
      setIsMounted(true);
      const today = new Date();
      const month = today.getMonth() + 1;
      const date = today.getDate();
      setIsSpecialDate(
        (month === 1 && date >= 23 && date <= 29) ||
          (month === 8 && date >= 12 && date <= 18),
      );
    }, []);

    const handleLoginModalOpen = () => {
      open();
      setModalKey("header");
    };

    const scrollDirection = useScrollDirection(10);
    const showNav = scrollDirection !== "down";

    const showSafariBottom = useScrollDirection(20) === "down";

    useEffect(() => {
      setIsMounted(true);
    }, []);

    const [uiState, dispatch] = useReducer(reducer, {
      isSidebarOpen: false,
      isSearchOpen: false,
      activeMenu: null as string | null,
      activeItem: true,
    });

    useEffect(() => {
      if (menuItems.length > 0 && !uiState.activeMenu) {
        dispatch({
          type: "toggleMenu",
          payload: menuItems[menuItems.length - 1].label,
        });
      }
    }, [menuItems]);

    const toggleMenu = useCallback((label: string) => {
      dispatch({ type: "toggleMenu", payload: label });
    }, []);

    const isSidebarOpenRef = useRef(uiState.isSidebarOpen);

    const toggleSidebar = useCallback(() => {
      dispatch({ type: "toggleSidebar" });
    }, []);

    const toggleSearch = useCallback(() => {
      dispatch({ type: "toggleSearch" });
    }, []);

    useEffect(() => {
      isSidebarOpenRef.current = uiState.isSidebarOpen;
    }, [uiState.isSidebarOpen]);

    useEffect(() => {
      if (isSidebarOpenRef.current) {
        dispatch({ type: "closeSidebar" });
      }
    }, [pathName]);

    useEffect(() => {
      if (!uiState.isSidebarOpen) return;
      if (isOpen) toggleSidebar();
    }, [uiState.isSidebarOpen, isOpen, toggleSidebar]);

    const handleLogout = () => {
      deleteCookie("authToken", { domain: ROOT_URL });
      deleteCookie("token", { domain: ROOT_URL });
      setLoggedIn(false);
    };

    if (
      HIDDEN_ROUTES.has(pathName) ||
      HIDDEN_ROUTE_PREFIXES.some((prefix) => pathName.startsWith(prefix))
    ) {
      return null;
    }

    return (
      <>
        <header
          className={`w-full fixed z-5000 transition-[transform,translate,padding,top,max-width,border-radius,border-color] duration-400 ease-in-out mx-auto left-0 right-0 border ${isScrolled ? "max-w-[96%] xs:max-w-[98%] 2xl:max-w-section bg-(--primary) rounded-[100px] top-2.5 border-[#C3D7F7]" : `2xl:max-w-full bg-(--header-bg) rounded-none px-0 top-0 border-(--header-bg)`} ${isTabDevice && (showNav ? "translate-y-0" : "translate-y-[-130%]")}`}
          style={{ touchAction: "manipulation", willChange: "transform" }}
        >
          <nav
            className={`w-full flex items-center justify-between transition-[padding,max-width] duration-350 ease-in-out mx-auto rounded-full ${isScrolled ? "max-w-section pr-4 pl-6 shadow-[0px_2px_6px_2px_#0000000A]" : "max-w-360 px-4 2lg:px-5"}`}
            style={{ willChange: "transform" }}
          >
            {/* Logo */}
            <a
              href="https://www.mysiponline.com/"
              className="w-150 xs:w-45 lg:w-42.5 flex items-center gap-2 shrink-0 xl:mr-16 lg:mr-9 mr-4"
              aria-label="MySIPOnline Logo"
            >
              {isMounted && isSpecialDate ? (
                <Image
                  src="/next_assets/images/common-images/header-tiranga-icon.svg"
                  alt="MySIPOnline Logo"
                  width={350}
                  height={100}
                  className="object-contain w-full h-auto"
                />
              ) : (
                <Icon.headerNewLogoIcon className="w-full text-white" />
              )}
              {/* <Icon.headerDiwaliLogoIcon className="transition w-full fill-white" /> */}
            </a>

            {!isTabDevice && (
              <>
                {/* Desktop Navigation */}
                <nav className="hidden 2md:flex gap-2.5 2lg:gap-6">
                  {menuItems.map((item) => {
                    const hasTag = item.submenu.some((sub) => Boolean(sub.tag));
                    return (
                      <div key={item.label} className="relative group">
                        <div className="relative font-normal text-2sm transition py-4.5 flex items-center cursor-pointer text-white group-hover:text-[#b9f559]">
                          {item.label}
                          {item.label === "Pro Services" && (
                            <Image
                              src={`${ROOT_URL}images/menu_icon/Pro_tag.svg`}
                              alt="Pro Services Icon"
                              width={25}
                              height={25}
                              className="ml-2"
                            />
                          )}
                          <Icon.dropdownDownArrow className="transition ml-1.5 fill-white group-hover:fill-[#B9F559] group-hover:rotate-180" />
                        </div>

                        {item.submenu && (
                          <div
                            className={[
                              "flex items-start absolute top-full mt-2 bg-white rounded-xl shadow-[0px_4px_10px_0px_#0000000D] opacity-0 invisible pointer-events-none group-hover:opacity-100 group-hover:visible group-hover:top-[85%] group-hover:pointer-events-auto transition min-w-max",
                              item.submenu.some(
                                (subItem) =>
                                  subItem.children &&
                                  subItem.children.length > 0,
                              ) || item.useWrapper
                                ? "left-0 translate-x-[-40%]"
                                : "-left-10",
                            ].join(" ")}
                          >
                            <span
                              className={[
                                "inline-block absolute top-0 w-0 h-0 border-l-16 border-r-16 border-b-16 border-transparent border-b-white -translate-y-1/2",
                                item.submenu.some(
                                  (subItem) =>
                                    subItem.children &&
                                    subItem.children.length > 0,
                                ) || item.useWrapper
                                  ? "left-[-16%] right-0 mx-auto"
                                  : "left-36 md:left-12",
                              ].join(" ")}
                            />

                            {item.useWrapper ? (
                              <div className="custom-wrapper flex items-stretch">
                                <div className="inline-block">
                                  <ul className="pt-2">
                                    {item.submenu.map((subItem) => (
                                      <li
                                        key={subItem.label}
                                        className={`min-w-85.5 relative last-of-type:rounded-b-lg border-b border-[#EEF0F6] last:border-b-0 transition ${subItem.background ?? "hover:bg-[#eaf3ff]"}`}
                                        style={{
                                          background: subItem.background,
                                        }}
                                      >
                                        <a
                                          href={`${subItem.isBlog ? BLOG_ROOT : ROOT_URL}${subItem.href}`}
                                          className="flex font-medium items-start gap-2 py-3 px-5 w-full text-gray-700"
                                          aria-label="Sub Items"
                                        >
                                          <Image
                                            src={`${ROOT_URL}images/menu_icon/${subItem.icon}`}
                                            alt="icon"
                                            width={25}
                                            height={25}
                                            className="shrink-0"
                                          />
                                          <div className="text-2sm font-medium leading-4.75 text-[#101010]">
                                            {subItem.label}
                                            <br />
                                            <p className="text-2xs font-normal block text-[#444444]">
                                              {subItem.subLabel}
                                            </p>
                                          </div>
                                        </a>

                                        {subItem.tag && (
                                          <span className="absolute top-3 right-1.5 bg-[#EC4D4D] text-[10px] leading-1 font-medium text-white uppercase tracking-wide px-3 py-0.5 rounded-full animate-pulse pointer-events-none">
                                            {subItem.tag}
                                          </span>
                                        )}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                                <div className="inline-block max-w-md w-full bg-[linear-gradient(177.86deg,#ffffff_1.8%,#d4e3fe_209.63%)] rounded-r-lg overflow-hidden">
                                  <a
                                    href="https://mutualfundtalk.com/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="Mutual Fund Talk"
                                  >
                                    <Image
                                      src={`${ROOT_URL}${item.useWrapper}`}
                                      alt="app-banner"
                                      width={500}
                                      height={400}
                                      className="w-full h-full object-cover"
                                    />
                                  </a>
                                </div>
                              </div>
                            ) : (
                              <div
                                className={
                                  item.submenu.some(
                                    (subItem) =>
                                      subItem.children &&
                                      subItem.children.length > 0,
                                  )
                                    ? "flex items-start"
                                    : "w-full"
                                }
                              >
                                <ul className="pt-2">
                                  {item.submenu
                                    .filter(
                                      (subItem) =>
                                        !subItem.children ||
                                        subItem.children.length === 0,
                                    )
                                    .map((subItem) => (
                                      <li
                                        key={subItem.label}
                                        className={`min-w-85.5 last-of-type:rounded-b-lg border-b border-[#EEF0F6] last:border-b-0 hover:bg-[#eaf3ff] relative transition ${subItem.background ?? "hover:bg-[#eaf3ff]"}`}
                                        style={{
                                          background: subItem.background,
                                        }}
                                      >
                                        <a
                                          href={`${subItem.isBlog ? BLOG_ROOT : ROOT_URL}${subItem.href}`}
                                          className="flex font-medium items-start gap-2 py-3 px-5 w-full"
                                        >
                                          <Image
                                            src={`${ROOT_URL}images/menu_icon/${subItem.icon}`}
                                            alt="icon"
                                            width={25}
                                            height={25}
                                            className="shrink-0"
                                            style={{ height: "auto" }}
                                          />
                                          <div className="text-2sm font-medium leading-4.75 text-[#101010]">
                                            {subItem.label}
                                            <br />
                                            <p className="text-2xs font-normal block text-[#444444]">
                                              {subItem.subLabel}
                                            </p>
                                          </div>
                                        </a>
                                        {subItem.tag && (
                                          <span className="absolute top-3 right-1.5 bg-[#EC4D4D] text-[10px] leading-1 font-medium text-white uppercase tracking-wide px-3 py-0.5 rounded-full animate-pulse pointer-events-none">
                                            {subItem.tag}
                                          </span>
                                        )}
                                      </li>
                                    ))}
                                </ul>

                                {item.submenu.some(
                                  (subItem) =>
                                    subItem.children &&
                                    subItem.children.length > 0,
                                ) && (
                                  <div className="border-l border-[#EEF0F6]">
                                    {item.submenu
                                      .filter(
                                        (subItem) =>
                                          subItem.children &&
                                          subItem.children.length > 0,
                                      )
                                      .map((group) => (
                                        <div key={group.label}>
                                          <p className="text-sm font-semibold text-primaryBlue uppercase p-4">
                                            {group.label}
                                          </p>
                                          <ul>
                                            {group.children &&
                                              group.children.map((child) => (
                                                <li
                                                  key={child.label}
                                                  className={`min-w-85.5 last-of-type:rounded-b-lg border-t border-[#EEF0F6] last:border-y hover:bg-[#eaf3ff] relative transition  ${group.background ?? "hover:bg-[#eaf3ff]"}`}
                                                >
                                                  <a
                                                    href={`${ROOT_URL}${child.href}`}
                                                    className="flex font-medium items-start gap-2 py-3 px-5 w-full"
                                                  >
                                                    <Image
                                                      src={`${ROOT_URL}images/menu_icon/${child.icon}`}
                                                      alt="icon"
                                                      width={25}
                                                      height={25}
                                                      className="shrink-0"
                                                      style={{ height: "auto" }}
                                                    />
                                                    <div className="text-2sm font-medium leading-4.75 text-[#101010]">
                                                      {child.label}
                                                      <br />
                                                      <p className="text-2xs font-normal block text-[#444444]">
                                                        {child.subLabel}
                                                      </p>
                                                    </div>
                                                  </a>
                                                  {child.tag && (
                                                    <span className="absolute top-3 right-1.5 bg-[#EC4D4D] text-[10px] leading-1 font-medium text-white uppercase tracking-wide px-3 py-0.5 rounded-full animate-pulse pointer-events-none">
                                                      {child.tag}
                                                    </span>
                                                  )}
                                                </li>
                                              ))}
                                          </ul>
                                        </div>
                                      ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        {hasTag && (
                          <span className="inline-block absolute top-1.5 -left-2.5 rotate-[-18deg] bg-[#EC4D4D] text-[8px] leading-1 font-medium text-white uppercase tracking-wide px-2 py-1 rounded-full pointer-events-none group-hover:opacity-0 transition-opacity">
                            {item.submenu
                              .filter((sub) => sub.tag)
                              .map((sub) => (
                                <div key={sub.id ?? sub.label}>{sub.tag}</div>
                              ))}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </nav>
              </>
            )}

            {/* Cart and Profile Icons */}
            <div className="flex gap-1.5 2lg:gap-2.5 items-center">
              {!isTabDevice && (
                <button
                  onClick={() => dispatchHelpModal(openHelpModal())}
                  className="hidden 2md:inline-block relative rounded-full font-semibold py-2.5 px-4 text-sm leading-4 border transition-all duration-400 ease-in-out bg-white text-(--text) border-white hover:bg-[#d8e7ff]"
                >
                  Need Help?
                </button>
              )}

              <button
                className="py-2 sm:py-3 m-0 cursor-pointer group"
                onClick={toggleSearch}
                aria-label="Search"
              >
                <Icon.headerSearchIcon2
                  className={`w-9 h-9 sm:w-10 sm:h-10 transition duration-300 rounded-full text-white border border-transparent group-hover:border group-hover:border-white`}
                />
              </button>

              <CartCountButton />

              {!isTabDevice && (
                <div className="hidden 2md:block relative group mr-0">
                  <button
                    className="inline-flex items-center py-3 m-0 cursor-pointer"
                    aria-label="My Account Button"
                  >
                    <Icon.headerProfileIcon
                      className={`transition-all duration-300 rounded-full text-white border border-transparent group-hover:border group-hover:border-white`}
                    />
                    <Icon.dropdownDownArrow
                      className={`transition ml-1.5 text-white group-hover:rotate-180`}
                    />
                  </button>

                  {/* Profile Dropdown */}
                  <div className="flex items-start absolute right-0 top-full mt-2 bg-white rounded-lg shadow-lg opacity-0 invisible pointer-events-none group-hover:opacity-100 group-hover:visible group-hover:top-[88%] group-hover:pointer-events-auto transition-all  ease-in-out min-w-max">
                    {/* Arrow Indicator */}
                    <span className="inline-block absolute top-0 right-4 w-0 h-0 border-l-16 border-r-16 border-b-16 border-transparent border-b-white -translate-y-1/2" />

                    {/* Menu Items */}
                    <div className="inline-block">
                      <ul className="pt-2">
                        {isMounted ? (
                          <>
                            {!isLoggedIn && (
                              <li className="border-b border-[#EEF0F6]">
                                <div className="flex items-center gap-3.5 py-3 px-4">
                                  <Link
                                    href="/login"
                                    className="block p-2 w-full text-center rounded-[50px] border border-(--secondary) font-normal text-base transition-all  ease-in-out bg-(--secondary) text-white shine-button-hover"
                                  >
                                    Sign In
                                  </Link>
                                  <Link
                                    href="/login"
                                    className="block p-2 w-full text-center rounded-[50px] border border-(--secondary) font-normal text-base transition bg-white text-(--text) hover:bg-(--secondary) hover:text-white"
                                  >
                                    Sign Up
                                  </Link>
                                </div>
                              </li>
                            )}

                            {profileMenuItems[0]?.submenu?.map((subItem) =>
                              subItem.id === "logout" ? (
                                <li
                                  key={subItem.id}
                                  onClick={handleLogout}
                                  className="min-w-250 last-of-type:rounded-b-lg px-5 border-b border-[#EEF0F6] last:border-b-0 hover:bg-[#eaf3ff] transition-all  ease-in-out"
                                >
                                  <a
                                    key={subItem.id}
                                    href={subItem.href ?? "#"}
                                    className="flex items-center gap-2 py-3 w-full text-2sm leading-4.75 font-medium text-[#101010]"
                                  >
                                    <Image
                                      src={`${ROOT_URL}images/menu_icon/${subItem.icon}`}
                                      alt={`${subItem.label} icon`}
                                      width={25}
                                      height={25}
                                      className="shrink-0"
                                    />
                                    {subItem.label}
                                  </a>
                                </li>
                              ) : (
                                <li
                                  key={subItem.id}
                                  className="min-w-250 last-of-type:rounded-b-lg px-5 border-b border-[#EEF0F6] last:border-b-0 hover:bg-[#eaf3ff] transition-all  ease-in-out"
                                >
                                  <a
                                    key={subItem.id}
                                    href={subItem.href ?? "#"}
                                    className="flex items-center gap-2 py-3 w-full text-2sm leading-4.75 font-medium text-[#101010]"
                                  >
                                    <Image
                                      src={`${ROOT_URL}images/menu_icon/${subItem.icon}`}
                                      alt={`${subItem.label} icon`}
                                      width={25}
                                      height={25}
                                      className="shrink-0"
                                    />
                                    {subItem.label}
                                  </a>
                                </li>
                              ),
                            )}
                          </>
                        ) : null}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Mobile Menu Button */}
              <button
                className="2md:hidden py-2 sm:py-3 group"
                onClick={toggleSidebar}
                aria-label="Side Drawer Button"
              >
                <Icon.headerDrawerIcon
                  isOpen={uiState.isSidebarOpen}
                  className={`w-9 h-9 sm:w-10 sm:h-10 transition duration-300 rounded-full text-white border border-transparent group-hover:border group-hover:border-white`}
                />
              </button>
            </div>
          </nav>
        </header>

        {/* Diwali Extra Decoration Code */}
        {/* <Image src={"/next_assets/images/diwali-lamps.svg"} alt="Diwali Decoration Items" width={181} height={252} className="fixed hidden pointer-events-none lg:block z-1 -top-3 left-5" />
      <Image src={"/next_assets/images/diwali-lamps.svg"} alt="Diwali Decoration Items" width={181} height={252} className="fixed hidden pointer-events-none lg:block z-1 -top-3 right-5 -scale-x-100" /> */}

        <HeaderSidebar
          isOpen={uiState.isSidebarOpen}
          isSafari={isSafari ?? false}
          menuItems={menuItems}
          activeMenu={uiState.activeMenu}
          toggleMenuAction={toggleMenu}
          toggleSidebarAction={toggleSidebar}
        />

        <HeaderSearchModal
          isOpen={uiState.isSearchOpen}
          onCloseAction={toggleSearch}
        />

        {isSafari && (
          <div
            className={`${showSafariBottom ? "hidden" : "block sm:hidden"} h-2 w-full fixed z-500 bottom-0 left-0 right-0 bg-white`}
          />
        )}
      </>
    );
  },
);

FloatingHeader.displayName = "FloatingHeader";

export default FloatingHeader;
